import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { computeUndisaggregatedResiduals } from '../components/data_explorer/undisaggregatedResiduals';
import { isAdPreviewColumn } from '../components/data_explorer/adPreviewColumn';
import { inferFormat, formatValue } from '../components/charts/chartUtils';
import { GRID_COLUMNS, rowsOfSpans } from '../components/informes/state/panelLayout';
import { PANEL_TYPES } from '../components/informes/state/informeModel';
import { joinPlatformNames, notApplicableMessage, notApplicableMetricsOf, platformName, summaryFilterNotesOf, tableFilterNotesOf } from './filterNotes';
import {
    GA4_CHANNEL, GA4_ROW, formatShare, ga4AssignedShare, ga4LabelColumn, ga4RowKind, isNotApplicableCell,
    notApplicableChannels, rowsToShow,
} from '../components/data_explorer/channelApplicability';
import { defaultTableOrdering, repeatedFirstColumnRows, sortTableRows } from '../components/data_explorer/tableOrdering';

// El «—» de una métrica que no se puede calcular con los filtros pedidos (tanda «Filtros que se
// respetan», review C-fe-07): el mismo que la pantalla, nunca la celda vacía del `null`.
const NOT_APPLICABLE_DASH = '—';

applyPlugin(jsPDF);

const IMAGE_CELL_MAX_PX = 240;
const IMAGE_CELL_MAX_HEIGHT_MM = 22;
const IMAGE_CELL_MAX_WIDTH_MM = 28;

// El `gap: 12px` de `.inf-grid` llevado a la hoja: sobre un folio de ~1200 px son ~1% del ancho,
// y el contenido útil del A4 apaisado mide 277 mm.
const PANEL_GAP_MM = 3;

// El breakpoint con el que `_informes.scss` colapsa `.inf-grid` a una sola columna
// (`@media (max-width: 992px) { .inf-grid > * { grid-column: 1 / -1 !important; } }`).
// Tiene que seguir a ese valor: ver `gridIsCollapsed` en `exportReportToPDF`.
const GRID_COLLAPSE_PX = 992;

// Controles que existen para OPERAR la pantalla y no dicen nada dentro de un archivo impreso: el
// grip de arrastre, los selects de dimensión/métrica, la ✕ de quitar el bloque y la barra del mapa
// de calor de la tabla. Se ocultan en el CLON que rasteriza html2canvas, así que la pantalla no se
// entera (QA-VIS-pdfgrilla-01 y -06: el chevron de las píldoras además se dibujaba encima de su
// propia etiqueta, y el PDF que recibe el cliente traía un botón «Editar» que no hace nada).
//
// ⚠️ El `:not(.-as-title)` NO es una afinación: en un KPI ese contenedor ES EL TÍTULO. `PanelShell`
// le pone `-as-title` cuando el select de métrica ocupa el lugar del rótulo (`metricAsTitle`), y el
// SCSS ya lo dibuja como el rótulo chico y gris — sin caja ni chevron en reposo, que es justo lo que
// html2canvas fotografía. Ocultarlo entero mandaba a producción tarjetas con el número solo: tres
// cifras sin decir cuál es Costo, cuál Impresiones y cuál Clicks. Medido sobre el PDF que genera la
// app, no deducido.
//
// `.field-help-icon-container` es el «?» de ayuda del encabezado de una columna: la primera tabla sale
// como texto, pero de la segunda en adelante se rasteriza y el «?» salía impreso sin abrir nada
// (review C-tabla-07).
const SCREEN_ONLY_SELECTORS = [
    '.inf-panel-grip',
    '.inf-panel-controls:not(.-as-title)',
    '.inf-panel-remove',
    '.chart-controls',
    '.field-help-icon-container',
    '[data-pdf-hide]',
].join(', ');

/** Saca de la copia rasterizada lo que sólo sirve en pantalla. Nunca toca el DOM vivo. */
function hideScreenOnlyControls(clonedDoc) {
    if (!clonedDoc || typeof clonedDoc.querySelectorAll !== 'function') return;
    clonedDoc.querySelectorAll(SCREEN_ONLY_SELECTORS).forEach((el) => {
        if (el && el.style) el.style.display = 'none';
    });
}

/**
 * Carga una URL de imagen y la convierte a data URL (JPEG) usando un canvas.
 * Devuelve null si la imagen no carga o el canvas queda tainted (CORS bloqueado).
 */
function loadImageAsDataUrl(url) {
    return new Promise((resolve) => {
        if (!url || typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
            resolve(null);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const timeout = setTimeout(() => resolve(null), 8000);
        img.onload = () => {
            clearTimeout(timeout);
            try {
                // Asset vacío o decode fallido (algunos thumbnails de Meta vencidos
                // disparan onload con naturalWidth=0). Sin esta validación, el canvas
                // queda en blanco y al exportar a JPEG sale como cuadrado negro.
                if (!img.naturalWidth || !img.naturalHeight) {
                    resolve(null);
                    return;
                }
                const ratio = Math.min(
                    IMAGE_CELL_MAX_PX / img.naturalWidth,
                    IMAGE_CELL_MAX_PX / img.naturalHeight,
                    1
                );
                const w = Math.max(1, Math.round(img.naturalWidth * ratio));
                const h = Math.max(1, Math.round(img.naturalHeight * ratio));
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                // Fondo blanco antes del drawImage: si el draw falla en silencio
                // (canvas tainted, etc.), la celda queda en blanco en vez de negra.
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                resolve({ dataUrl, width: w, height: h });
            } catch (e) {
                resolve(null);
            }
        };
        img.onerror = () => {
            clearTimeout(timeout);
            resolve(null);
        };
        img.src = url;
    });
}

/**
 * Extrae la URL string de un valor de celda (puede venir como objeto con formattedValue).
 */
function extractCellString(cellValue) {
    if (cellValue === null || cellValue === undefined) return '';
    if (typeof cellValue === 'object') {
        if (cellValue.formattedCurrentValue !== undefined && cellValue.formattedCurrentValue !== null) {
            return String(cellValue.formattedCurrentValue);
        }
        if (cellValue.formattedValue !== undefined && cellValue.formattedValue !== null) {
            return String(cellValue.formattedValue);
        }
        return '';
    }
    return String(cellValue);
}

/**
 * Pre-carga todas las imágenes de columnas con dataType === 'IMAGE' en paralelo.
 * @returns {Map<string, {dataUrl, width, height}>} - cache url → imagen embebible
 */
async function preloadImagesForRows(headers, rows) {
    const imageColumnKeys = headers
        .filter(h => h && h.dataType === 'IMAGE')
        .map(h => h.key);
    if (imageColumnKeys.length === 0) return new Map();

    const urls = new Set();
    for (const row of rows) {
        for (const key of imageColumnKeys) {
            const url = extractCellString(row[key]);
            if (url) urls.add(url);
        }
    }
    if (urls.size === 0) return new Map();

    const entries = await Promise.all(
        Array.from(urls).map(async (url) => [url, await loadImageAsDataUrl(url)])
    );
    const cache = new Map();
    for (const [url, value] of entries) {
        if (value) cache.set(url, value);
    }
    return cache;
}

/**
 * Arma el `tableData` que consume exportReportToPDF a partir de la respuesta del Explorador.
 *
 * La comparación se detecta por la presencia de `comparison` en `dateRangeInfo`, que es la
 * MISMA señal que usa la tabla en pantalla para rotular los períodos
 * (DataExplorerTable.formatPeriodLabel). El backend NUNCA emitió un `isComparison`:
 * `_build_date_range_info` (core/views/data_explorer.py) arma sólo `main`, `comparison` y
 * `warning` — ver DATA_EXPLORER_API_SPEC.md §5. Leer una clave inexistente daba siempre
 * `false` y el PDF salía sin un solo delta aunque la pantalla los mostrara (card 1PAM4akd).
 *
 * @param {Object} results - Respuesta del Explorador tal como la guarda DataExplorerPage
 * @param {Object} options - Lo que pide el panel de Informes, para que el PDF salga como la pantalla:
 *                           `hideEmptyRows` (`panel.options.hide_empty_rows`), `stableOrder` (el orden de
 *                           arranque de la tabla de Informes, `tableOrdering.js`) y `groupByFirstColumn`
 *                           (`panel.options.group_by_first_column`). El Explorador no pasa ninguna.
 * @returns {Object|null} - tableData para exportReportToPDF, o null si no hay resultados
 */
export function buildPdfTableData(results, options = {}) {
    if (!results) return null;

    return {
        hideEmptyRows: !!(options && options.hideEmptyRows),
        stableOrder: !!(options && options.stableOrder),
        groupByFirstColumn: !!(options && options.groupByFirstColumn),
        headers: results.tableData?.headers || [],
        rows: results.tableData?.rows || [],
        summary: results.summary || {},
        isComparison: !!(results.dateRangeInfo && results.dateRangeInfo.comparison),
        // Las notas de filtro del nodo: el Explorador las trae como `filter_notes` y el panel de
        // Informes como `filterNotes` (`panelData.tableResults`). Sin notas, `null`. Las del Total
        // salen de la consulta de totales y pueden decir otra cosa (card x4TLNeOw): sin clave propia,
        // son las mismas de la tabla.
        filterNotes: tableFilterNotesOf(results),
        summaryFilterNotes: summaryFilterNotesOf(results),
    };
}

/**
 * Las claves (`metric_<id>`) de las columnas que NO se pueden calcular con los filtros pedidos, y
 * el motivo de cada una para la nota al pie. Sin notas, vacío.
 */
export function notApplicableColumns(headers, filterNotes, t, fieldsData) {
    const notApplicable = notApplicableMetricsOf(filterNotes);
    const keys = new Set();
    const notes = [];
    (headers || []).forEach((header) => {
        if (!header || !header.key || !header.key.startsWith('metric_')) return;
        const fieldId = header.key.replace(/^metric_/, '');
        if (!notApplicable.has(fieldId)) return;
        keys.add(header.key);
        const motivo = typeof t === 'function' ? notApplicableMessage(filterNotes, fieldId, t, fieldsData) : null;
        notes.push(motivo ? `${header.label}: ${motivo}` : header.label);
    });
    return { keys, notes };
}

/**
 * Lo que la regla de «no aplica» por FILA (`channelApplicability.js`) suma a la nota al pie: el
 * mismo texto que en pantalla va en el ⓘ del encabezado de cada métrica (en qué plataformas no aplica
 * y, en GA4, cuánto quedó asignado a campañas) y el de la fila «GA4 · sin campaña de pauta». Sin la
 * dimensión Plataforma ni métricas GA4 con Campaña, vacío.
 */
export function rowRuleFootnotes(headers, rows, t, fieldsData, locale = 'es') {
    // Sin `t` (una llamada vieja, un test), el texto por defecto: nunca la clave cruda.
    const tr = typeof t === 'function'
        ? t
        : (key, params) => (typeof params === 'string' ? params : (params && params.defaultValue) || key);
    const noEscape = {interpolation: {escapeValue: false}};
    const notes = [];
    (headers || []).forEach((header) => {
        if (!header || !header.key || !header.key.startsWith('metric_')) return;
        const channels = notApplicableChannels(rows, header.key, fieldsData);
        if (channels.length) {
            const platforms = joinPlatformNames(channels, tr);
            notes.push(tr('informes.table.not_applicable_tip', {
                metric: header.label, platforms, ...noEscape,
                defaultValue: `${header.label} no aplica a ${platforms}.`,
            }));
        }
        const share = ga4AssignedShare(rows, headers, header.key, fieldsData);
        if (share !== null) {
            const percent = formatShare(share, locale);
            notes.push(`${header.label}: ${tr('informes.ga4.assigned_share', {
                percent, ...noEscape, defaultValue: `${percent} asignado a campañas`,
            })}`);
        }
    });
    if ((rows || []).some((row) => ga4RowKind(row, headers, fieldsData) === GA4_ROW.UNATTRIBUTED)) {
        const label = tr('informes.ga4.unattributed_row', {defaultValue: 'GA4 · sin campaña de pauta'});
        const tip = tr('informes.ga4.unattributed_row_tip', {defaultValue: 'GA4 que esta tabla no pudo asignar a una campaña: tráfico directo, orgánico, email, referidos o campañas cuyo GA4 todavía no se reparte.'});
        notes.push(`${label}: ${tip}`);
    }
    return notes;
}

/** El rótulo de la fila de GA4 en el PDF, igual que en pantalla (`DataExplorerTable.renderGa4RowLabelCell`). */
function ga4RowLabel(kind, translate) {
    return kind === GA4_ROW.UNATTRIBUTED
        ? translate('informes.ga4.unattributed_row', 'GA4 · sin campaña de pauta')
        : platformName(GA4_CHANNEL);
}

/**
 * Traductor para los textos que el PDF escribe por su cuenta.
 *
 * `exportReportToPDF` recibe la `t` de la pantalla en el `context` (DataExplorerPage ya la
 * tiene de props). Si no viene —una llamada vieja, un test— cae al literal en español, que es
 * el comportamiento que tenía el módulo antes de internacionalizarse: nunca una key cruda.
 */
function makeTranslator(t) {
    return (key, fallback) => {
        if (typeof t !== 'function') return fallback;
        const traducido = t(key, fallback);
        return typeof traducido === 'string' && traducido !== key ? traducido : fallback;
    };
}

/**
 * Deja el `fileBaseName` que manda la pantalla en algo que sirva de nombre de archivo.
 *
 * Hoy las dos pantallas pasan un literal, pero es un parámetro público de un exportador compartido:
 * el día que alguien le pase el título que escribió el usuario, una barra o dos puntos rompen el
 * `save()` en Windows. Se queda con letras, números, guion y guion bajo; si no queda nada, el
 * llamador decide el default.
 */
function sanitizeFileBaseName(name) {
    if (typeof name !== 'string') return '';
    return name.trim().replace(/[^\w-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

/**
 * Etiqueta para el delta cuando el período comparativo no tuvo actividad.
 *
 * El backend manda "∞" / "-∞" en `formattedDelta` cuando el valor anterior fue 0 y el actual
 * no (`_calculate_delta`, core/views/data_explorer.py). En pantalla ese glifo se ve bien, pero
 * el PDF se escribe con Helvetica estándar (WinAnsiEncoding), que NO lo tiene: jsPDF emite los
 * dos bytes crudos del code point (0x22 0x1E) y en el visor sale una comilla seguida de un
 * carácter de control. Verificado sobre el PDF generado, no asumido.
 */
const DELTA_WITHOUT_BASELINE_KEY = 'data_explorer.pdf_delta_without_baseline';
const DELTA_WITHOUT_BASELINE_FALLBACK = 'nuevo';

/** Interlineado por defecto de jsPDF, para calcular cuánto ocupa el valor principal. */
const DELTA_LINE_HEIGHT_FACTOR = 1.15;

/** Aire entre el final del valor y la baseline del delta que lo acompaña. */
const DELTA_BASELINE_GAP_MM = 1.5;

/**
 * Deja el delta en caracteres que la fuente del PDF pueda dibujar.
 */
function sanitizeDeltaForPdf(formattedDelta, translate) {
    if (typeof formattedDelta !== 'string') return '';
    return formattedDelta.includes('∞')
        ? translate(DELTA_WITHOUT_BASELINE_KEY, DELTA_WITHOUT_BASELINE_FALLBACK)
        : formattedDelta;
}

/**
 * Texto del delta de una celda, o '' si esa celda no tiene comparación que mostrar.
 *
 * El caso "los dos períodos en cero" lo resuelve igual que la tabla en pantalla
 * (`DataExplorerTable.renderSummaryRow`): el backend deja `formattedDelta` en null porque no
 * hay división posible, pero la lectura correcta es que no hubo variación, no que falte el
 * dato. La pantalla muestra un `0%` ahí y el PDF tiene que decir lo mismo.
 */
function resolveDeltaLine(cellValue, translate) {
    const {currentValue, comparisonValue, formattedDelta} = cellValue;

    const sinMovimiento = currentValue === 0
        && (comparisonValue === 0 || comparisonValue === null || comparisonValue === undefined);
    if (sinMovimiento) return '0%';

    return formattedDelta ? sanitizeDeltaForPdf(formattedDelta, translate) : '';
}

/**
 * Obtiene el valor formateado de una celda para el PDF.
 * Reutiliza la lógica de getFormattedValueForCSV de DataExplorerPage.
 * @param {*} cellValue - Valor de la celda (puede ser objeto con comparison data)
 * @param {boolean} isComparison - Si estamos en modo comparación
 * @param {Function} translate - Traductor del PDF (ver makeTranslator)
 * @returns {string|{mainLine: string, deltaLine: string}} - String simple o objeto con líneas
 */
function getFormattedCellValue(cellValue, isComparison, translate) {
    if (cellValue === null || cellValue === undefined) return '';

    // Valor con datos de comparación
    if (typeof cellValue === 'object' && cellValue.formattedCurrentValue !== undefined) {
        const mainLine = cellValue.formattedCurrentValue !== null
            ? cellValue.formattedCurrentValue
            : '';
        if (isComparison) {
            const deltaLine = resolveDeltaLine(cellValue, translate);
            if (deltaLine) return { mainLine, deltaLine };
        }
        return mainLine;
    }

    // Valor formateado simple (dimensiones y métricas sin comparación)
    if (typeof cellValue === 'object' && cellValue.formattedValue !== undefined) {
        return cellValue.formattedValue !== null ? cellValue.formattedValue : '';
    }

    return String(cellValue);
}

/**
 * Construye la fila de resumen (totales) para la tabla.
 * Reutiliza la lógica de getSummaryValueForColumn de DataExplorerTable.
 * @param {Array} headers - Headers de la tabla [{key, label}, ...]
 * @param {Object} summary - Objeto con valores de resumen por fieldId
 * @param {boolean} isComparison - Si estamos en modo comparación
 * @param {Function} translate - Traductor del PDF (ver makeTranslator)
 * @returns {Object|null} - Fila de resumen o null si no hay summary
 */
function buildSummaryRow(headers, summary, isComparison, translate, notApplicableKeys = new Set()) {
    if (!summary || Object.keys(summary).length === 0) return null;

    const row = {};
    let isFirstDimension = true;

    for (const header of headers) {
        const key = header.key;

        if (!key.startsWith('metric_')) {
            // Columna de dimensión. Misma key que la fila Total de la tabla en pantalla.
            row[key] = isFirstDimension ? translate('data_explorer.total', 'Total') : '';
            isFirstDimension = false;
            continue;
        }

        // Una métrica que no aplica con los filtros: «—», como el Total en pantalla, aunque el
        // summary traiga un número (el Backend puede fusionar el total sin filtrar).
        if (notApplicableKeys.has(key)) {
            row[key] = NOT_APPLICABLE_DASH;
            continue;
        }

        // Columna de métrica: buscar en summary
        const fieldId = key.replace(/^metric_/, '');
        let summaryValue = summary[fieldId];

        // Fallback case-insensitive
        if (!summaryValue) {
            const matchingKey = Object.keys(summary).find(
                k => k.toLowerCase() === fieldId.toLowerCase()
            );
            if (matchingKey) summaryValue = summary[matchingKey];
        }

        if (summaryValue) {
            row[key] = getFormattedCellValue(summaryValue, isComparison, translate);
        } else {
            row[key] = '';
        }
    }

    return row;
}

/**
 * Fila "Sin desglose": residuo Total − suma de las filas visibles (ej. PMax no tiene
 * anuncios individuales y no aparece en el desglose, pero sí cuenta en el Total).
 * Misma lógica compartida que la fila de la tabla en pantalla.
 *
 * @returns {Object|null} - Fila formateada o null si no hay residuo significativo
 */
function buildUndisaggregatedRow(headers, summary, rows, fieldsData, translate, notApplicableKeys = new Set()) {
    // Una columna que no aplica no tiene residuo (review C-fe-08): con el summary sin filtrar y las
    // celdas en «—», el total entero saldría como «Sin desglose».
    const headersThatApply = headers.filter((h) => !notApplicableKeys.has(h.key));
    const residuals = computeUndisaggregatedResiduals(headersThatApply, summary, rows, fieldsData);
    if (!residuals) return null;

    const row = {};
    let isFirstDimension = true;

    for (const header of headers) {
        const key = header.key;

        if (!key.startsWith('metric_')) {
            // Misma key que la fila equivalente de la tabla en pantalla.
            row[key] = isFirstDimension
                ? translate('data_explorer.undisaggregated_label', 'Sin desglose')
                : '';
            isFirstDimension = false;
            continue;
        }

        const entry = residuals[key];
        if (entry) {
            const format = inferFormat(entry.fieldId);
            let text = formatValue(entry.residual, format);
            if (entry.comparisonResidual !== null) {
                text += ` vs ${formatValue(entry.comparisonResidual, format)}`;
            }
            row[key] = text;
        } else {
            row[key] = '';
        }
    }

    return row;
}

/**
 * Construye estilos de alineación y ancho por columna para autoTable.
 *
 * Distribución de anchos:
 *   - Columnas IMAGE: ancho fijo (suficiente para la imagen embebida).
 *   - Métricas vs dimensiones: peso relativo (DIM_RATIO → cada dimensión
 *     ocupa 60% más que cada métrica), porque las dimensiones suelen
 *     contener strings largos sin espacios (URLs, nombres de campaña con
 *     slashes) que de otra forma se llevan todo el ancho disponible.
 *
 * @param {Array} headers - Headers de la tabla
 * @param {number} contentWidth - Ancho útil del PDF en mm (page - margins)
 * @returns {Object} - columnStyles para jspdf-autotable
 */
function buildColumnStyles(headers, contentWidth) {
    const DIM_RATIO = 1.6;
    const imageWidth = IMAGE_CELL_MAX_WIDTH_MM + 4;

    const numImageCols = headers.filter(h => h && h.dataType === 'IMAGE').length;
    const reserved = numImageCols * imageWidth;
    const available = Math.max(0, contentWidth - reserved);

    const metricCount = headers.filter(
        h => h && h.dataType !== 'IMAGE' && h.key && h.key.startsWith('metric_')
    ).length;
    const dimensionCount = headers.length - numImageCols - metricCount;

    const totalUnits = metricCount + dimensionCount * DIM_RATIO;
    const unit = totalUnits > 0 ? available / totalUnits : 0;
    const metricWidth = unit;
    const dimensionWidth = unit * DIM_RATIO;

    const styles = {};
    headers.forEach((h, i) => {
        const isImage = h && h.dataType === 'IMAGE';
        const isMetric = h && h.key && h.key.startsWith('metric_');
        styles[i] = {
            halign: isImage ? 'center' : (isMetric ? 'right' : 'left'),
        };
        if (isImage) {
            styles[i].cellWidth = imageWidth;
            styles[i].minCellWidth = imageWidth;
        } else if (isMetric) {
            styles[i].cellWidth = metricWidth;
        } else {
            styles[i].cellWidth = dimensionWidth;
        }
    });
    return styles;
}

/**
 * Exporta el reporte del Explorador de Datos como PDF.
 * Charts se renderizan como JPEG comprimido, la tabla como texto nativo.
 * @param {Object} refs - { lineChartRef, chartsSectionRef }
 * @param {Object} context - { clientName, dateRange, dimensions, metrics, fieldsData, t,
 *                             pdfTitle, fileBaseName } — los dos últimos, opcionales: con qué
 *                             encabezado y con qué nombre de archivo sale. Sin ellos, los del
 *                             Explorador, que es lo que hacía este módulo cuando era suyo solo.
 * @param {Object} tableData - { headers, rows, summary, isComparison }
 */
export async function exportReportToPDF(refs, context = {}, tableData = null) {
    const { lineChartRef, chartsSectionRef, panelRefs } = refs;
    const translate = makeTranslator(context.t);

    // `panelRefs` (Informes sobre bloques): un ref por panel, en el orden del folio. Sin él, las
    // dos secciones del Explorador de siempre.
    //
    // Los que no montaron se descartan DESPUÉS de armar las filas, no antes: sacarlos acá los
    // borraba del arreglo y los paneles siguientes subían a su fila, o sea que un bloque que no
    // renderizó recompaginaba el resto del PDF (review C-pdf-kpi-04). El hueco se preserva igual
    // que para un panel que falla al rasterizarse.
    const chartSections = (Array.isArray(panelRefs) && panelRefs.length > 0
        ? panelRefs
        : [chartsSectionRef, lineChartRef]
    ).filter(Boolean);

    const hasCharts = chartSections.some(ref => ref && ref.current);
    const hasTable = tableData && tableData.headers && tableData.headers.length > 0;

    if (!hasCharts && !hasTable) return;

    // Paneles que no se pudieron rasterizar (canvas tainted, timeout de imagen…).
    const skippedPanels = [];
    const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        // Los content streams van deflateados. No es cosmético: un informe con una tabla larga
        // los escribe como texto plano y el archivo se va a MEGABYTES de operadores sin
        // comprimir — el PDF del 08/09 pesaba 5,7 MB y sólo el 5% eran las imágenes (medido).
        compress: true,
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // Header naranja
    pdf.setFillColor(255, 71, 15); // #FF470F
    pdf.rect(0, 0, pageWidth, 18, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(255, 255, 255);
    // El encabezado y el nombre del archivo los puede fijar quien llama, porque este exportador sirve
    // a DOS pantallas: bajado desde `/informes` dec\u00eda \u00abInforme \u2014 Explorador de Datos\u00bb y se guardaba
    // como `reporte-explorador-\u2026`, o sea que nombraba un m\u00f3dulo que no es y escrib\u00eda \u00abReportes\u00bb, que
    // no se dice en ning\u00fan texto que el usuario lea. El Explorador no pasa nada y conserva los suyos.
    const headerTitle = context.pdfTitle
        || translate('data_explorer.pdf_report_title', 'Informe \u2014 Explorador de Datos');
    pdf.text(headerTitle, margin, 11);

    const { clientName, dateRange } = context;
    if (clientName || dateRange) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(255, 220, 200);
        const subtitle = [clientName, dateRange].filter(Boolean).join('  \u00b7  ');
        pdf.text(subtitle, pageWidth - margin, 11, { align: 'right' });
    }

    let currentY = 22;

    // --- Charts como JPEG, en la MISMA grilla de 12 columnas que la pantalla ---
    //
    // Cada bloque se dibuja al ancho que le toca por su `span` (una métrica destacada es 3 de 12:
    // cuatro por fila). Antes TODOS iban a `contentWidth`, así que un KPI angosto se estiraba al
    // ancho de la hoja y su alto crecía en la misma proporción: tres números ocupaban dos páginas
    // (card U3w64uUZ, reportado el 08/09). Estirar ×4 también repartía los 498 px de la captura
    // sobre 277 mm — unos 45 DPI, de ahí que además se viera borroso.
    //
    // Un ref SIN `span` vale 12 y ocupa el ancho completo: así el Explorador de siempre
    // (`chartsSectionRef` + `lineChartRef`) sale exactamente igual que antes.
    const columnWidth = (contentWidth - PANEL_GAP_MM * (GRID_COLUMNS - 1)) / GRID_COLUMNS;
    const widthOfSpan = (span) => span * columnWidth + (span - 1) * PANEL_GAP_MM;

    // ⚠️ El span vale mientras la grilla EN PANTALLA esté en 12 columnas. Abajo de 992px el CSS la
    // colapsa a una sola, y html2canvas captura lo que hay en el DOM: un KPI mide ahí el ancho
    // entero (proporción ~8:1) y dibujarlo en 67 mm lo deja en una tira de ~8 mm de alto, con el
    // número en ~4,6 pt. Ilegible — y PEOR que antes de la grilla, porque ese mismo canvas se
    // dibujaba a 277 mm y se leía. Mientras la captura no fuerce el layout de escritorio, en esa
    // franja se exporta como se exportaba antes: todo a ancho completo (review C-pdf-kpi-02).
    const gridIsCollapsed = typeof window !== 'undefined'
        && typeof window.innerWidth === 'number'
        && window.innerWidth <= GRID_COLLAPSE_PX;
    const spanOf = (ref) => (gridIsCollapsed
        ? GRID_COLUMNS
        : Math.min(Math.max(1, (ref && ref.span) || GRID_COLUMNS), GRID_COLUMNS));

    // Cuánto tardó cada panel en rasterizarse. El export tarda ~50 s y hasta ahora no había forma
    // de saber en qué bloque se iban: viaja en el return para poder medirlo sin instrumentar.
    const panelTimings = [];

    // `InformesPage` manda la PRIMERA tabla del folio por separado, en `tableData`, para que salga
    // como texto seleccionable y paginable en vez de una captura. Pero esa misma tabla está también
    // en `panelRefs` (el folio los manda todos), así que sin este salteo el cliente la recibe DOS
    // VECES: rasterizada en su fila y otra vez debajo con autoTable — verificado sobre el PDF del
    // 09/09, la imagen ocupaba y[75,200] mm y el texto arrancaba en y=64,9 (review C-pdf-kpi-01).
    // Se saltea UNA sola: si el informe tiene más tablas, las demás siguen saliendo como imagen en
    // su lugar, que es mejor que perderlas (`tableData` sólo lleva la primera).
    let tableAlreadyAsText = false;
    const goesToAutoTable = (ref) => {
        if (!hasTable || tableAlreadyAsText || !ref || ref.type !== PANEL_TYPES.TABLE) return false;
        tableAlreadyAsText = true;
        return true;
    };
    const rasterSections = chartSections.map(ref => (goesToAutoTable(ref) ? {...ref, current: null} : ref));

    const rows = rowsOfSpans(rasterSections.map(spanOf));

    for (const row of rows) {
        // Se captura de a una fila: sostener los canvas de TODOS los paneles a la vez para poder
        // componerlos multiplicaría la memoria de un informe largo. Una fila son 4 como mucho.
        const items = [];
        let consumedSpan = 0;
        for (let i = row.startIndex; i <= row.endIndex; i++) {
            const ref = rasterSections[i];
            const span = spanOf(ref);
            const offsetSpan = consumedSpan;
            consumedSpan += span;
            if (!ref || !ref.current) continue;

            // `imageTimeout` acotado: el default de html2canvas son 15 s POR sección, y un informe
            // con varios paneles que traen imágenes externas (los creativos de Meta de la columna
            // Imagen/video) puede quedarse minutos sin dar señal de vida. Mejor un panel incompleto
            // que una pantalla que parece colgada.
            const startedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
            try {
                const canvas = await html2canvas(ref.current, {
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#ffffff',
                    scale: 1.5,
                    logging: false,
                    imageTimeout: 5000,
                    onclone: hideScreenOnlyControls,
                });
                // ⚠️ `toDataURL` va DENTRO del try. Con `allowTaint: true`, una imagen de otro
                // origen sin cabeceras CORS —la miniatura de un creativo— se dibuja igual pero deja
                // el canvas «tainted», y entonces esta línea tira `SecurityError: Tainted canvases
                // may not be exported` (verificado en pantalla el 07/09). Estando afuera, ese throw
                // rompía el export ENTERO: el usuario apretaba «Descargar PDF» y no bajaba nada.
                // Ahora se saltea ese panel y el informe se descarga sin él.
                const imgData = canvas.toDataURL('image/jpeg', 0.85);
                const width = widthOfSpan(span);
                items.push({
                    imgData,
                    x: margin + offsetSpan * (columnWidth + PANEL_GAP_MM),
                    width,
                    height: (canvas.height * width) / canvas.width,
                });
            } catch (e) {
                // Saltear en silencio devuelve un PDF con un panel menos y nadie se entera.
                // Queda el rastro en consola, y el conteo viaja en el return para que quien
                // llama pueda decírselo al usuario.
                skippedPanels.push(i + 1);
                console.warn(`[pdfExport] panel ${i + 1} omitido: ${e && e.name}: ${e && e.message}`);
            } finally {
                const endedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
                panelTimings.push({panel: i + 1, span, ms: Math.round(endedAt - startedAt)});
            }
        }

        if (!items.length) continue;

        // El alto de la fila lo fija el bloque más alto: en pantalla el grid los estira parejos,
        // así que capturados por separado ya vienen con el mismo alto, pero un panel que se
        // salteó o un canvas con otra proporción no pueden pisar al de al lado.
        let rowHeight = Math.max(...items.map((it) => it.height));
        let scaleFactor = 1;

        if (currentY + rowHeight > pageHeight - margin) {
            // No entra en lo que queda: página nueva (nunca cortar un bloque por la mitad).
            pdf.addPage();
            currentY = margin;
        }

        const fullPageContentHeight = pageHeight - margin * 2;
        if (rowHeight > fullPageContentHeight) {
            // Más alta que una hoja entera: se achica la fila COMPLETA, para no romper la grilla.
            scaleFactor = fullPageContentHeight / rowHeight;
            rowHeight = fullPageContentHeight;
        }

        // Al achicar se centra sobre lo que la fila OCUPA, no sobre el ancho útil de la hoja: una
        // fila de media grilla centrada contra los 277 mm se corría de más a la derecha
        // (review C-pdf-kpi-06).
        const rowWidth = Math.max(...items.map((it) => it.x + it.width)) - margin;
        const xOffset = scaleFactor < 1 ? (rowWidth * (1 - scaleFactor)) / 2 : 0;
        for (const item of items) {
            pdf.addImage(
                item.imgData, 'JPEG',
                margin + xOffset + (item.x - margin) * scaleFactor, currentY,
                item.width * scaleFactor, item.height * scaleFactor,
            );
        }
        currentY += rowHeight + 6;
    }

    // --- Tabla como texto nativo ---
    if (hasTable) {
        const { headers, rows: allRows, summary, isComparison, filterNotes } = tableData;
        // Las mismas filas que la pantalla: sin las vacías si el panel lo pide. «Sin desglose» se
        // calcula sobre TODAS (`allRows`), como en pantalla.
        const visibleRows = rowsToShow(allRows, headers, context.fieldsData, tableData.hideEmptyRows);
        // En el orden con que arranca la tabla de Informes (la primera dimensión, desempatada por las
        // demás) y con la primera celda repetida en blanco si el panel agrupa. El orden que el usuario
        // haya elegido con un clic en pantalla no viaja: el Explorador tampoco lo manda, sale crudo.
        const ordering = tableData.stableOrder ? defaultTableOrdering(headers, visibleRows) : null;
        const rows = ordering
            ? sortTableRows(visibleRows, headers, ordering, context.fieldsData, {tieBreak: true})
            : visibleRows;
        const repeatedRows = tableData.groupByFirstColumn && ordering
            ? repeatedFirstColumnRows(rows, headers, ordering, context.fieldsData)
            : new Set();
        const ga4Column = ga4LabelColumn(headers);

        // Las columnas que no aplican con los filtros (tanda «Filtros que se respetan»): «—» en cada
        // celda y en el Total, y el motivo como nota al pie de la tabla. Sin notas, nada de esto existe.
        // Las celdas siguen a las notas de la tabla y el Total a las de los totales (card x4TLNeOw); una
        // llamada sin `summaryFilterNotes` usa las de la tabla para los dos, como antes.
        const notApplicable = notApplicableColumns(headers, filterNotes || null, context.t, context.fieldsData);
        const summaryNotes = 'summaryFilterNotes' in tableData ? tableData.summaryFilterNotes : filterNotes;
        const totalNotApplicable = notApplicableColumns(headers, summaryNotes || null, context.t, context.fieldsData);
        // «Sin desglose» resta las filas del Total: sin sentido si cualquiera de los dos es «—».
        const withoutResidual = new Set([...notApplicable.keys, ...totalNotApplicable.keys]);
        const footnotes = [...new Set([
            ...notApplicable.notes, ...totalNotApplicable.notes,
            ...rowRuleFootnotes(headers, rows, context.t, context.fieldsData, context.locale),
        ])];

        // Pre-cargar imágenes (columnas con dataType === 'IMAGE') en paralelo
        const imageColumnKeys = new Set(
            headers.filter(h => h && h.dataType === 'IMAGE').map(h => h.key)
        );
        const imageCache = await preloadImagesForRows(headers, rows);

        // Preparar body: summary row primero (+ fila "Sin desglose" si aplica), luego data rows
        const bodyRows = [];
        const summaryRow = buildSummaryRow(headers, summary, isComparison, translate, totalNotApplicable.keys);
        const hasSummary = summaryRow !== null;

        if (hasSummary) {
            bodyRows.push(summaryRow);
        }

        const undisaggregatedRow = buildUndisaggregatedRow(headers, summary, allRows, context.fieldsData, translate, withoutResidual);
        const undisaggregatedRowIndex = undisaggregatedRow !== null ? bodyRows.length : -1;
        if (undisaggregatedRow !== null) {
            bodyRows.push(undisaggregatedRow);
        }

        const repeatedBodyRows = new Set();
        rows.forEach((row, rowIndex) => {
            const formattedRow = {};
            const ga4Kind = ga4Column ? ga4RowKind(row, headers, context.fieldsData) : null;
            headers.forEach((header) => {
                if (notApplicable.keys.has(header.key)) {
                    formattedRow[header.key] = NOT_APPLICABLE_DASH;
                } else if (isNotApplicableCell(row, header.key, context.fieldsData)) {
                    // La métrica no corresponde a la plataforma de esta fila: «—», como en pantalla.
                    formattedRow[header.key] = NOT_APPLICABLE_DASH;
                } else if (ga4Kind && header.key === ga4Column) {
                    formattedRow[header.key] = ga4RowLabel(ga4Kind, translate);
                } else if (isAdPreviewColumn(header)) {
                    // «Ver anuncio» con un link clicable, como en pantalla (la URL es opaca).
                    formattedRow[header.key] = {
                        __isAdPreview: true,
                        url: extractCellString(row[header.key]),
                    };
                } else if (imageColumnKeys.has(header.key)) {
                    // Marcamos celdas de imagen para tratarlas en didParseCell/didDrawCell
                    formattedRow[header.key] = {
                        __isImage: true,
                        url: extractCellString(row[header.key]),
                    };
                } else {
                    formattedRow[header.key] = getFormattedCellValue(row[header.key], isComparison, translate);
                }
            });
            if (repeatedRows.has(rowIndex)) repeatedBodyRows.add(formattedRow);
            bodyRows.push(formattedRow);
        });

        // Determinar fontSize según cantidad de columnas
        const baseFontSize = headers.length > 12 ? 6 : 7;
        const headerFontSize = headers.length > 12 ? 6.5 : 7.5;

        // Si no hay espacio suficiente para al menos el header de la tabla, nueva página
        if (currentY > pageHeight - 30) {
            pdf.addPage();
            currentY = margin;
        }

        // Conservar el rótulo hasta dibujar: autoTable necesita medirlo y puede comenzar una página
        // en medio de un grupo. Sólo ocultamos repeticiones después de la primera fila de esa página.
        let bodyPage = null;
        let firstBodyRowOnPage = null;
        const hiddenGroupTexts = new WeakMap();
        pdf.autoTable({
            startY: currentY,
            columns: headers.map(h => ({ header: h.label, dataKey: h.key })),
            body: bodyRows,
            theme: 'grid',
            margin: { left: margin, right: margin },
            // Si la fila no entra entera (típicamente por la imagen embebida),
            // pasarla completa a la siguiente página en vez de partirla.
            rowPageBreak: 'avoid',
            styles: {
                font: 'helvetica',
                fontSize: baseFontSize,
                cellPadding: isComparison ? 3 : 2,
                overflow: 'linebreak',
                lineColor: [220, 220, 220],
                lineWidth: 0.2,
            },
            headStyles: {
                fillColor: [255, 71, 15],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: headerFontSize,
                halign: 'center',
            },
            alternateRowStyles: {
                fillColor: [249, 249, 249],
            },
            columnStyles: buildColumnStyles(headers, contentWidth),
            didParseCell: (data) => {
                const raw = data.cell.raw;

                if (typeof raw === 'object' && raw !== null && raw.__isAdPreview) {
                    data.cell.text = raw.url ? [translate('data_explorer.see_ad', 'Ver anuncio')] : [];
                    if (raw.url) data.cell.styles.textColor = [0, 123, 255];
                    return;
                }

                // Celdas de imagen: vaciar texto y reservar altura si la imagen cargó
                if (typeof raw === 'object' && raw !== null && raw.__isImage) {
                    data.cell.text = [];
                    const cached = raw.url ? imageCache.get(raw.url) : null;
                    if (cached) {
                        data.cell.styles.minCellHeight = IMAGE_CELL_MAX_HEIGHT_MM + 2;
                    } else if (raw.url) {
                        // Fallback: si no se pudo cargar, mantener URL como texto
                        data.cell.text = [raw.url];
                    }
                    return;
                }

                // Manejar comparison cells: extraer mainLine como texto
                if (typeof raw === 'object' && raw !== null && raw.mainLine !== undefined) {
                    data.cell.text = [raw.mainLine];
                }

                // Estilizar summary row (index 0 si existe)
                if (hasSummary && data.section === 'body' && data.row.index === 0) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }

                // Estilizar fila "Sin desglose" (muted/itálica, como en pantalla)
                if (undisaggregatedRowIndex !== -1 && data.section === 'body' && data.row.index === undisaggregatedRowIndex) {
                    data.cell.styles.fontStyle = 'italic';
                    data.cell.styles.textColor = [108, 117, 125];
                    data.cell.styles.fillColor = [248, 249, 250];
                }
            },
            willDrawCell: (data) => {
                if (data.section !== 'body' || data.column.index !== 0) return;
                if (bodyPage !== data.pageNumber) {
                    bodyPage = data.pageNumber;
                    firstBodyRowOnPage = data.row.index;
                }
                if (repeatedBodyRows.has(data.row.raw) && data.row.index !== firstBodyRowOnPage) {
                    hiddenGroupTexts.set(data.cell, data.cell.text);
                    data.cell.text = [];
                }
            },
            didDrawCell: (data) => {
                if (hiddenGroupTexts.has(data.cell)) {
                    // Restaurar por si autoTable vuelve a usar la celda al partir una fila muy alta.
                    data.cell.text = hiddenGroupTexts.get(data.cell);
                    hiddenGroupTexts.delete(data.cell);
                    return; // Tampoco dibujar la imagen o el enlace de una repetición oculta.
                }
                if (data.section !== 'body') return;

                const raw = data.cell.raw;

                if (typeof raw === 'object' && raw !== null && raw.__isAdPreview) {
                    if (raw.url) {
                        try {
                            pdf.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url: raw.url });
                        } catch (e) {
                            // Sin link la celda igual dice «Ver anuncio»: no rompemos el PDF por esto.
                        }
                    }
                    return;
                }

                // Dibujar imágenes en celdas de tipo IMAGE
                if (typeof raw === 'object' && raw !== null && raw.__isImage && raw.url) {
                    const cached = imageCache.get(raw.url);
                    if (!cached) return;
                    const cellPadding = 1.5;
                    const maxW = Math.max(1, Math.min(IMAGE_CELL_MAX_WIDTH_MM, data.cell.width - cellPadding * 2));
                    const maxH = Math.max(1, Math.min(IMAGE_CELL_MAX_HEIGHT_MM, data.cell.height - cellPadding * 2));
                    const ratio = Math.min(maxW / cached.width, maxH / cached.height, 1);
                    const drawW = cached.width * ratio;
                    const drawH = cached.height * ratio;
                    const x = data.cell.x + (data.cell.width - drawW) / 2;
                    const y = data.cell.y + (data.cell.height - drawH) / 2;
                    try {
                        pdf.addImage(cached.dataUrl, 'JPEG', x, y, drawW, drawH);
                    } catch (e) {
                        // Si falla el embed, no rompemos el resto del PDF
                    }
                    return;
                }

                if (typeof raw !== 'object' || !raw || !raw.deltaLine) return;

                // Dibujar deltaLine en gris pequeño debajo del valor principal
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(5.5);
                pdf.setTextColor(130, 130, 130);

                const cellX = data.cell.x;
                const cellWidth = data.cell.width;
                const cellY = data.cell.y;
                const cellHeight = data.cell.height;

                // Alinear a la derecha para métricas, izquierda para dimensiones
                const isMetric = headers[data.column.index]?.key?.startsWith('metric_');
                const textX = isMetric
                    ? cellX + cellWidth - 2
                    : cellX + 2;
                const align = isMetric ? 'right' : 'left';

                // El delta va pegado a SU valor, no al piso de la celda: el alto de la fila lo
                // fija la celda más alta (una dimensión que envuelve, una imagen), así que
                // anclarlo al borde inferior lo despegaba del número que califica.
                // OJO: getTextPos() devuelve el TOP del bloque de texto (cell.y + padding), no
                // la baseline — hay que bajar el alto de las líneas que ocupa el valor, si no
                // el delta se dibuja ENCIMA. Para una celda de una línea el resultado coincide
                // con el `cellHeight - 1.5` de antes; la diferencia está en las filas altas.
                const textPos = typeof data.cell.getTextPos === 'function' ? data.cell.getTextPos() : null;
                let textY = cellY + cellHeight - 1.5;
                if (textPos && typeof textPos.y === 'number') {
                    const mainLines = Array.isArray(data.cell.text) ? Math.max(1, data.cell.text.length) : 1;
                    const lineHeightMm = (data.cell.styles.fontSize / 72) * 25.4 * DELTA_LINE_HEIGHT_FACTOR;
                    textY = Math.min(
                        textPos.y + mainLines * lineHeightMm + DELTA_BASELINE_GAP_MM,
                        cellY + cellHeight - 1
                    );
                }

                pdf.text(raw.deltaLine, textX, textY, { align });
            },
        });

        // La nota al pie con el motivo de cada «—»: lo que en pantalla dice el ⓘ del encabezado.
        if (footnotes.length) {
            const finalY = (pdf.lastAutoTable && pdf.lastAutoTable.finalY) || currentY;
            let noteY = finalY + 4;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(6.5);
            pdf.setTextColor(108, 117, 125);
            for (const note of footnotes) {
                const lines = typeof pdf.splitTextToSize === 'function' ? pdf.splitTextToSize(note, contentWidth) : [note];
                const blockHeight = lines.length * 3 + 1.5;
                if (noteY + blockHeight > pageHeight - margin) {
                    pdf.addPage();
                    noteY = margin;
                }
                pdf.text(lines, margin, noteY);
                noteY += blockHeight;
            }
        }
    }

    const today = new Date().toISOString().split('T')[0];
    pdf.save(`${sanitizeFileBaseName(context.fileBaseName) || 'reporte-explorador'}-${today}.pdf`);
    // Quien llama decide qué hacer con los omitidos; el Explorador ignora el retorno y se
    // comporta igual que antes.
    return {skippedPanels, panelTimings};
}
